import React, { useState, useMemo, useCallback } from "react";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Input,
} from "reactstrap";
import "./listDropdown.css";
import PropTypes from "prop-types";

const ListDropdown = (props) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchText, setSearchText] = useState("");

  let { listItems, stopPropagation, searchable } = props;

  const toggle = (e) => {
    if (stopPropagation) {
      e.stopPropagation();
    }
    if (dropdownOpen) setSearchText("");
    setDropdownOpen((prevState) => !prevState);
  };

  for (let i = 0; i < listItems.length; i++) {
    listItems[i].key = i;
  }

  const visibleItems = useMemo(() => {
    if (!searchable || !searchText.trim()) return listItems;

    const lower = searchText.toLowerCase();
    const result = [];
    let pendingHeader = null;

    for (const item of listItems) {
      if (item.hidden) continue;

      if (item.divider) {
        result.push(item);
        continue;
      }

      if (item.header) {
        pendingHeader = item;
        continue;
      }

      const matches = !item.text || item.text.toLowerCase().includes(lower);
      if (matches) {
        if (pendingHeader) {
          result.push(pendingHeader);
          pendingHeader = null;
        }
        result.push(item);
      }
    }

    return result;
  }, [listItems, searchText, searchable]);

  const className = props.className ?? "";
  const toggleClassName = props.toggleClass ?? "";
  const menuClassName = props.toggleMenu ?? "";

  const renderItemContent = (item) => {
    const baseClass = item.checkbox === true ? "ml-4" : "";

    if (item.description) {
      const contentClass = ["list-dropdown-item-content", baseClass]
        .filter(Boolean)
        .join(" ");
      return (
        <div className={contentClass}>
          <span className="list-dropdown-item-title">{item.text}</span>
          <span className="list-dropdown-item-description">
            {item.description}
          </span>
        </div>
      );
    }

    return <span className={baseClass}>{item.text}</span>;
  };

  const itemsToRender =
    searchable && searchText.trim() ? visibleItems : listItems;

  const hasCheckboxes = useMemo(
    () => listItems.some((item) => item.checkbox === true),
    [listItems],
  );
  const hasAnySelected = useMemo(
    () => listItems.some((item) => item.checkbox && item.checkboxStatus),
    [listItems],
  );
  const handleClearAll = useCallback(
    (e) => {
      e.stopPropagation();
      listItems
        .filter((item) => item.checkbox && item.checkboxStatus)
        .forEach((item) => item.command());
    },
    [listItems],
  );

  return (
    <Dropdown
      className={className}
      style={props.style}
      isOpen={dropdownOpen}
      toggle={toggle}
    >
      <DropdownToggle
        caret={props.caret ?? true}
        className={toggleClassName}
        color={props.color}
        disabled={props.disabled}
      >
        {props.title}
      </DropdownToggle>
      <DropdownMenu
        className={`${menuClassName}${searchable ? " searchable" : ""}`}
        container={props.menuContainer}
        strategy={props.menuContainer ? "fixed" : undefined}
      >
        {searchable && (
          <div
            className="dropdown-search-container"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dropdown-search-input-wrapper">
              <Input
                type="text"
                className="dropdown-search-input"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                autoFocus
              />
              {hasCheckboxes && (
                <button
                  className="dropdown-clear-btn"
                  disabled={!hasAnySelected}
                  onClick={handleClearAll}
                >
                  <i className="fas fa-eraser" />
                </button>
              )}
            </div>
          </div>
        )}
        {itemsToRender
          .filter((item) => !item.hidden)
          .map((item) => {
            if (item.divider) {
              return <DropdownItem divider key={item.key} />;
            }

            if (item.header) {
              return (
                <div key={item.key}>
                  <DropdownItem
                    className={`dropdown-header ${item.className ?? ""}`}
                    onClick={item.command}
                    toggle={!item.checkbox}
                    disabled={item.disabled}
                  >
                    {item.checkbox === true ? (
                      <Input
                        type="checkbox"
                        className="ml-1"
                        checked={item.checkboxStatus}
                        disabled={item.checkboxDisabled}
                        onChange={() => {}}
                      />
                    ) : null}{" "}
                    <span className={item.checkbox === true ? "ml-4" : ""}>
                      {item.text.toUpperCase()}
                    </span>
                  </DropdownItem>
                </div>
              );
            }
            
            return (
              <DropdownItem
                key={item.key}
                onClick={item.command}
                toggle={!item.checkbox}
                disabled={item.disabled}
              >
                {item.checkbox === true ? (
                  <Input
                    type="checkbox"
                    className="ml-1"
                    checked={item.checkboxStatus}
                    disabled={item.checkboxDisabled}
                    onChange={() => {}}
                  />
                ) : null}
                {renderItemContent(item)}
              </DropdownItem>
            );
          })}
      </DropdownMenu>
    </Dropdown>
  );
};

export default ListDropdown;

ListDropdown.propTypes = {
  title: PropTypes.node.isRequired,
  listItems: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      text: PropTypes.node,
      description: PropTypes.node,
      command: PropTypes.func,
      disabled: PropTypes.bool,
      hidden: PropTypes.bool,
      header: PropTypes.bool,
      divider: PropTypes.bool,
      checkbox: PropTypes.bool,
      checkboxStatus: PropTypes.bool,
      checkboxDisabled: PropTypes.bool,
      className: PropTypes.string,
    }),
  ).isRequired,
  color: PropTypes.string,
  disabled: PropTypes.bool,
  caret: PropTypes.bool,
  searchable: PropTypes.bool,
  stopPropagation: PropTypes.bool,
  className: PropTypes.string,
  toggleClass: PropTypes.string,
  toggleMenu: PropTypes.string,
  // Renders the menu in a portal (e.g. "body") instead of next to the toggle, so a
  // scrollable/overflow-hidden ancestor cannot clip it. Paired with Popper's fixed
  // strategy, which keeps the menu anchored once it no longer shares the ancestor.
  menuContainer: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  style: PropTypes.object,
};
